import React, { useEffect, useMemo, useRef } from 'react';
import { Frame } from '../templates/Frame/Frame';
import { Frameless } from '../templates/Frameless/Frameless';
import { AppContext, Provider as AppContextProvider } from '../../contexts/AppContext/AppContext';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { useGenericPage } from './useGenericPage';
import { GlobalMenuStore } from '../../contexts/GlobalMenuStore';
import { Composition } from '../../types/api/Composition';
import { isHidden } from '../../lib/BlockUtils/BlockUtils';
import { PageLockedModal } from '../../_lib/components/PageLockedModal/PageLockedModal';
import { ReadAllAccessBanner } from '../../_lib/components/ReadAllAccessBanner/ReadAllAccessBanner';

type GenericPageProperties = {
  composition: Composition;
};

const getHasPageManagement = (composition: Composition) => {
  const pageManagementBlocks = composition?.sections?.pageManagement?.blocks;
  if (!pageManagementBlocks || isHidden(pageManagementBlocks)) {
    return false;
  }

  if (pageManagementBlocks) {
    const editModeAvailable = composition.editModeInfo.available;
    const pageManagement = pageManagementBlocks.at(0);
    if (pageManagement?.embedded) {
      const { views } = pageManagement.embedded;
      // the defaultView.editModeToggle url is always provided by the block, but may only be considered if the global
      // composition.editModeInfo.available flag is true
      // (i.e. there is at least one block on the page which is editable).
      const hasSonEditModeLink = !!(editModeAvailable && views?.default?.embedded?.editModeToggle);
      const hasNonDefaultViews = !!Object.keys(views).filter(v => v !== 'default').length;
      const hasOn2EditLink = !!views?.default.embedded?.edit;
      const hasPageState = !!views?.default.embedded?.pageState;
      return hasNonDefaultViews || hasSonEditModeLink || hasOn2EditLink || hasPageState;
    }
  }

  return false;
};

export function GenericPage({ composition }: GenericPageProperties) {
  const { windowInnerWidth, accessKeysEnabled, disableAccessKeys, resetAccessKeys, enableAccessKeys } =
    useGenericPage(composition);
  const contentBehindModalRef = useRef<HTMLDivElement>(null);
  const GenericP = useMemo<AppContext>(
    () =>
      ({
        userInfo: {
          ...composition.userInfo,
          settings: {
            accessKeysEnabled,
            colorScheme: composition.userInfo?.settings?.colorScheme,
          },
        },
        disableAccessKeys,
        enableAccessKeys,
        resetAccessKeys,
        editModeInfo: composition.editModeInfo,
        appVersion: composition.appVersion,
        portalConfiguration: composition.portalConfiguration,
        headMetaInfo: composition.headMetaInfo,
        pageInfo: composition.pageInfo,
        availableSections: {
          pageManagement: getHasPageManagement(composition),
          navigation: !!composition?.sections?.navigation,
        },
        windowInnerWidth,
        publicSessionKey: composition.publicSessionKey,
      }) as AppContext,
    [accessKeysEnabled, windowInnerWidth, composition, disableAccessKeys, enableAccessKeys, resetAccessKeys]
  );

  /**
   * We need the offset between the header's top and the viewport's top because there can be content, above the header,
   * like ReadAllAccessBanner.tsx, see SON-18326.
   * We need this offset for calculating the scroll container height (.content), see _layout.scss
   */
  useEffect(() => {
    const updateHeaderOffset = () => {
      const contentContainer = contentBehindModalRef.current;
      if (contentContainer) {
        const offsetInPx = contentContainer.getBoundingClientRect().top;
        const roundedPx = Math.max(0, Math.round(offsetInPx));
        const offsetInRem = roundedPx / 16;
        document.documentElement.style.setProperty('--header-offset', `${offsetInRem}rem`);
      }
    };
    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);

    return () => window.removeEventListener('resize', updateHeaderOffset);
  }, []);

  return (
    <AppContextProvider value={GenericP}>
      {composition.userInfo?.readAllMode && composition.userInfo?.disableReadAllModeEndpoint?.url ? (
        <ReadAllAccessBanner disableReadAllModeRoute={composition.userInfo?.disableReadAllModeEndpoint?.url} />
      ) : null}
      <GlobalMenuStore>
        <div id={GlobalDocumentIds.genericPageTemplate} className="content-behind-modal" ref={contentBehindModalRef}>
          {composition.template === 'Frameless' ? (
            <Frameless composition={composition} />
          ) : (
            <Frame composition={composition} />
          )}
        </div>
      </GlobalMenuStore>
      {composition.editModeInfo.active && composition.pageInfo?.pageLocked === true ? <PageLockedModal /> : null}
    </AppContextProvider>
  );
}
